import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * LandingFaq — Felix-style accordion for the public marketing page.
 * Only ONE panel is open at a time; tapping an open item closes it.
 *
 * Copy is deliberately conservative: no CTAs, no rate claims, no
 * connect-your-bank promises. This block sits directly above the trust
 * footer on Landing so a visitor's last impression is "helper tool,
 * confirm with your accountant" — not marketing bravado.
 *
 * DO NOT reword the answers without founder + accountant sign-off. Same
 * rules as PublicTrustFooter: Australian English, no rate specifics,
 * never imply lodgement.
 */
const ITEMS = [
  {
    q: "Do I need to be good with computers?",
    a: "No. Snap the receipt, check the numbers, send the pack. That's the job.",
  },
  {
    q: "What if my trade isn't listed?",
    a: "You don't pick a trade pack. If you have an ABN and buy stuff for jobs, it works.",
  },
  {
    q: "Does it lodge my BAS or tax return?",
    a: "No. It's a helper. You get worksheets and an exception list for your accountant. They (or you) lodge in myGov.",
  },
  {
    q: "Does it connect to Xero or MYOB?",
    a: "Not yet. Your accountant still gets a PDF pack they can work from.",
  },
  {
    q: "Phone or computer?",
    a: "Built for the phone. Add it to your Home Screen. Browser works too.",
  },
  {
    q: "Can my crew use it?",
    a: "One login, your jobs. Team logins come later.",
  },
  {
    q: "Is my data safe?",
    a: "Only you see your receipts and jobs. We don't sell your data. Always confirm figures before you send a pack.",
  },
  {
    q: "What does it cost?",
    a: "Soft launch — free while we get packs right with real tradies and accountants. We'll say before anything is paid.",
  },
  {
    q: "What do I send my accountant?",
    a: "BAS worksheet, FY summary, TPAR draft if you have subs, receipts in the pack, and a list of what's missing (no photo, no job, no ABN).",
  },
];

export default function LandingFaq({ testId = "landing-faq" }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      data-testid={testId}
      className="relative z-10 py-14 md:py-20 border-t border-white/[0.06] bg-[#1a1a1a]"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Eyebrow + heading — matches the section-heading style used on
            the rest of the landing page: orange eyebrow above a large
            plain wordmark heading. */}
        <div
          data-testid={`${testId}-eyebrow`}
          className="text-[11px] font-heading font-bold uppercase tracking-[0.24em] text-[#FF9500]"
        >
          Questions
        </div>
        <h2
          data-testid={`${testId}-title`}
          className="mt-2 font-heading font-black text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-white"
        >
          Questions?
        </h2>

        <ul className="mt-8 space-y-3">
          {ITEMS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <li
                key={item.q}
                data-testid={`${testId}-item-${i}`}
                className={`rounded-2xl border transition-colors ${
                  isOpen
                    ? "border-[#FF9500]/30 bg-[#111214]"
                    : "border-white/[0.06] bg-[#1F1F1F] hover:border-white/[0.12]"
                }`}
              >
                <button
                  type="button"
                  data-testid={`${testId}-toggle-${i}`}
                  aria-expanded={isOpen}
                  aria-controls={`${testId}-panel-${i}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 text-left"
                >
                  <span className="font-heading font-semibold text-white text-[16px] md:text-[17px] leading-snug">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 shrink-0 text-white/50 transition-transform duration-200 ${
                      isOpen ? "rotate-180 text-[#FF9500]" : ""
                    }`}
                    strokeWidth={2.4}
                  />
                </button>
                {isOpen && (
                  <div
                    id={`${testId}-panel-${i}`}
                    data-testid={`${testId}-panel-${i}`}
                    className="px-4 md:px-5 pb-5 -mt-1 text-[14px] md:text-[15px] leading-relaxed text-white/75"
                  >
                    {item.a}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
